/* eslint-disable @atlassian/relay/unused-fields */
import { useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type { ApolloClient } from 'apollo-client';
import { useQuery } from '@apollo/react-hooks';
import type { Extension, GQLExtension } from '../graphql/types';
import {
	isGQLGatewayError,
	isGQLUnderlyingServiceError,
	isRelayNetworkError,
} from '../graphql/types';
import { useMetricsContext } from '../../context';
import { FetchPreFilteredForgeAppsQuery } from './lib/queries';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
import { createAtlAttributionHeader } from '../../utils';
import type {
	GQLExtensionRenderingContextInput,
	PreFilteredExtensionsQueryHookOptions,
	PrefilteredExtensionsResult,
	PreFilteredExtensionsVariables,
	UseExtensionListRelayHookResult,
	UsePreFilteredExtensionListHookResult,
	UsePreFilteredExtensionsQueryOptions,
} from './useExtensionList';
import { transformPreFilteredExtensions } from './lib/transformForgeExtensions';
import type { usePrefilteredExtensionList_forge_ui_Query as extensionListQuery } from './__generated__/usePrefilteredExtensionList_forge_ui_Query.graphql';
export interface PrefilteredExtensionsGQLResult<T> {
	data?: { confluence_forgeExtensionsByType?: ReadonlyArray<T | null> | null };
	loading: boolean;
}

export const createUsePrefilteredExtensionListQueryOptions = <T>(
	cloudId: string,
	context: GQLExtensionRenderingContextInput,
	client: ApolloClient<object> | undefined,
	types: string[],
	locale?: string,
	includeHidden?: boolean,
	queryOptions?: PreFilteredExtensionsQueryHookOptions,
	contextIds: string[] = [cloudId],
): T => {
	const variables = {
		cloudId,
		context,
		includeHidden: includeHidden ?? false,
		types,
		locale,
	};

	return {
		variables,
		...queryOptions,
		client,
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
			},
			allowOnExternalPage: true,
		},
	} as unknown as T;
};

export const usePrefilteredExtensionList = ({
	cloudId,
	context,
	includeHidden,
	locale,
	types,
	client,
	queryOptions,
}: UsePreFilteredExtensionsQueryOptions): UsePreFilteredExtensionListHookResult => {
	const requestOptions =
		createUsePrefilteredExtensionListQueryOptions<UsePreFilteredExtensionsQueryOptions>(
			cloudId,
			context,
			client,
			types,
			locale,
			includeHidden,
			queryOptions,
		);

	const { error, loading, data } = useQuery<
		PrefilteredExtensionsResult,
		PreFilteredExtensionsVariables
	>(FetchPreFilteredForgeAppsQuery, requestOptions);
	const extensions = getPrefilteredExtensionsGQLResult({ loading, data });

	const { page } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (extensions) {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'succeeded',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'usePrefilteredExtensionList',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, page, createAnalyticsEvent]);

	useEffect(() => {
		if (
			// if hydration of appOwners (non-critical data) fails but we still have the extensions,
			// we shouldn't count this as a bad event
			!extensions &&
			error &&
			error.graphQLErrors.length > 0
		) {
			const err = error.graphQLErrors.find(
				(err) => isGQLUnderlyingServiceError(err) || isGQLGatewayError(err),
			);

			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'usePrefilteredExtensionList',
						errorName: err
							? isGQLUnderlyingServiceError(err)
								? 'componentApi'
								: 'componentGraphQL'
							: 'uncaught',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, error, page, createAnalyticsEvent]);

	return {
		error,
		extensions: extensions || [],
		loading,
	};
};

export const getPrefilteredExtensionsGQLResult = <T>({
	loading,
	data,
}: PrefilteredExtensionsGQLResult<T>): Extension[] | null => {
	const extensions = (data?.confluence_forgeExtensionsByType || []) as unknown as Extension[];
	if (!loading && extensions.length > 0) {
		return transformPreFilteredExtensions(extensions);
	}
	return null;
};

const fetchRequests = new Map<string, Promise<GQLExtension[] | null>>();

export const usePrefilteredExtensionListRelay = ({
	cloudId,
	context,
	includeHidden,
	locale,
	types,
}: UsePreFilteredExtensionsQueryOptions): UseExtensionListRelayHookResult => {
	const environment = useRelayEnvironment();
	const [extensions, setExtensions] = useState<GQLExtension[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const { page } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const cacheKey = JSON.stringify({ cloudId, context, includeHidden, locale, types });

		if (!fetchRequests.has(cacheKey)) {
			const extensions = new Promise<GQLExtension[] | null>((resolve, reject) => {
				fetchQuery<extensionListQuery>(
					environment,
					graphql`
						query usePrefilteredExtensionList_forge_ui_Query(
							$cloudId: ID!
							$context: ConfluenceExtensionRenderingContextInput
							$includeHidden: Boolean
							$locale: String
							$types: [String]
						) {
							confluence_forgeExtensionsByType(
								cloudId: $cloudId
								context: $context
								includeHidden: $includeHidden
								locale: $locale
								types: $types
							) {
								id
								appId
								appVersion
								appOwner
								environmentId
								environmentType
								environmentKey
								consentUrl
								migrationKey
								userAccess {
									enabled
									hasAccess
								}
								key
								properties {
									key
									value
								}
								egress {
									addresses
									category
									type
									inScopeEUD
								}
								scopes
								license {
									active
									capabilitySet
									billingPeriod
									ccpEntitlementId
									ccpEntitlementSlug
									isEvaluation
									subscriptionEndDate
									trialEndDate
									type
								}
								type
								installationId
								hiddenBy
							}
						}
					`,
					{
						cloudId,
						// @ts-ignore - the generated types convert GQL Long to a number, which may truncate the id. The type will be changed to string
						context,
						includeHidden,
						locale,
						types,
					},
					{
						networkCacheConfig: {
							metadata: {
								headers: {
									'atl-attribution': createAtlAttributionHeader(cloudId),
								},
								allowOnExternalPage: true,
							},
						},
					},
				).subscribe({
					next: (data) => {
						resolve(
							getPrefilteredExtensionsGQLResult({
								data,
								loading: false,
							}),
						);
					},
					error: (error: Error) => {
						fetchRequests.delete(cacheKey);
						reject(error);
					},
				});
			});
			fetchRequests.set(cacheKey, extensions);
		}

		fetchRequests
			.get(cacheKey)!
			.then((extensions) => {
				setExtensions(extensions);
				setLoading(false);
			})
			.catch((error: Error) => {
				setError(error);
				setLoading(false);
			});
	}, [cloudId, context, includeHidden, locale, types, environment]);

	useEffect(() => {
		if (extensions) {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'succeeded',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'usePrefilteredExtensionList',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, page, createAnalyticsEvent]);

	useEffect(() => {
		if (
			// if hydration of appOwners (non-critical data) fails but we still have the extensions,
			// we shouldn't count this as a bad event
			!extensions &&
			error &&
			isRelayNetworkError(error)
		) {
			const err = error.source.errors.find(
				(err) => isGQLUnderlyingServiceError(err) || isGQLGatewayError(err),
			);

			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'useExtensionList',
						errorName: err
							? isGQLUnderlyingServiceError(err)
								? 'componentApi'
								: 'componentGraphQL'
							: 'uncaught',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, error, page, createAnalyticsEvent]);

	return {
		extensions: extensions || [],
		loading,
		error,
	};
};
